import {VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {ChildLayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/ChildLayoutModel';
import {LayoutType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutType';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BoxView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/BoxView';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes,
    IComponentStateContext
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {ObjectUtils} from '@mintdata/mint-kit/impl/util/ObjectUtils';
import * as classnames from 'classnames/bind';
import {computed, untracked} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {CSSProperties} from 'react';

const styles = require('./HostView.less');

const cx = classnames.bind(styles);

export interface HostViewProps
{
    hostId:string;
    boxId:string;
    visualBox:VisualBox;
    repeater:Repeater;
    isViewportInOverlays:boolean;
    isViewportOnActivePage:boolean;
    allowAnimation:boolean;
    isDesignMode?:boolean;
    isReactive?:boolean;
}

interface HostViewState
{
    needAddAnimationClass:boolean;
}

@observer
export class HostView extends BaseComponentWithStateContext<HostViewProps, HostViewState>
{
    public static readonly childContextTypes = componentStateContextTypes;

    public static defaultProps:Partial<HostViewProps> = {
        isReactive: true,
        isViewportInOverlays: false,
        isViewportOnActivePage: false
    };

    state:HostViewState = {
        needAddAnimationClass: false
    };

    private _refHandlers = {
        host: (ref:HTMLElement | null) =>
        {
            const {visualBox, repeater, hostId} = this.props;

            if( ref )
            {
                if( repeater.levels.every(({index}) => index === 0) )
                    controllers.ui.surfaceArea.setBoxHostRef(visualBox.box, hostId, ref);

                controllers.ui.surfaceArea.setRepeatedBoxHostRef(visualBox.box, repeater, hostId, ref);
            }
            else
                controllers.ui.surfaceArea.deleteRepeatedBoxHostRef(visualBox.box, repeater, hostId);
        }
    };

    @computed
    private get layoutCss():CSSProperties
    {
        const {repeater, visualBox} = this.props;

        const layout:ChildLayoutModel = visualBox.repeatedData.get(repeater.index).childLayout;

        return layout && LayoutType.isHVBox(layout.layoutType) && layout.hvBox
            ? layout.hvBox.toCss()
            : {};
    }

    public shouldComponentUpdate(nextProps:HostViewProps, nextState:HostViewState, nextContext:IComponentStateContext)
    {
        return !ObjectUtils.shallowCompareKeys(this.props, nextProps) ||
            !ObjectUtils.shallowCompareKeys(this.context, nextContext);
    }

    public componentDidUpdate(prevProps:Readonly<HostViewProps>, prevState:Readonly<HostViewState>, prevContext:any):void
    {
        if( this.props.allowAnimation )
        {
            if( !prevProps.isViewportOnActivePage && this.props.isViewportOnActivePage )
                this.setState((prevState:HostViewState) => ({needAddAnimationClass: true}));
        }
        else if( this.state.needAddAnimationClass )
        {
            this.setState((prevState:HostViewState) => ({needAddAnimationClass: false}));
        }
    }

    public render():JSX.Element
    {
        if( this.props.isReactive )
            return this._render();

        return untracked(() => this._render());
    }

    private _render():JSX.Element
    {
        const {isDesignMode, isViewportInOverlays, hostId, boxId} = this.props;
        const {needAddAnimationClass} = this.state;

        return (
            <div
                data-host-id={hostId}
                data-host-box-id={boxId}
                id={'hostView'}
                ref={this._refHandlers.host}
                className={cx('hostView', 'contentHolder', {
                    pointerEventsNone: isViewportInOverlays,
                    appearingPage: needAddAnimationClass,
                    isDesignMode
                })}
                style={this.layoutCss}
            >
                {this._renderChildren()}
            </div>
        );
    }

    private _getHostChildren():Array<VisualBox>
    {
        const {visualBox, hostId, repeater} = this.props;

        const hostChildren:Array<VisualBox> = !hostId
            ? visualBox.repeatedData.get(repeater.index).children
            : visualBox.repeatedData.get(repeater.index).children.filter(
                (child:VisualBox) => child && child.box.hostId == hostId
            );

        return hostChildren;
    }

    private _renderChildren():Array<JSX.Element>
    {
        const boxes:Array<JSX.Element> = [];

        this._getHostChildren().forEach((childVisualBox:VisualBox) =>
        {
            childVisualBox.repeaters.forEach((repeater:Repeater) =>
            {
                boxes.push(
                    <BoxView
                        key={`${childVisualBox.box.id}_${repeater.index}`}
                        isReactive={this.props.isReactive}
                        visualBox={childVisualBox}
                        repeater={repeater}
                    />
                );
            });
        });

        return boxes;
    }
}
